import React, { useCallback, useRef, useEffect } from 'react';
import { TabsProps, TabBarState } from './iTabs'

// 定义 action 类型 - 简化为只使用 BATCH_UPDATE
type TabBarAction =
    | { type: 'BATCH_UPDATE'; payload: Partial<TabBarState> };

// reducer 函数 - 简化版本
export const tabBarReducer = (state: TabBarState, action: TabBarAction): TabBarState => {
    switch (action.type) {
        case 'BATCH_UPDATE':
            return { ...state, ...action.payload };
        default:
            return state;
    }
};

export function setTransform(style: React.CSSProperties, v?: string) {
    style.transform = v;
    style.WebkitTransform = v;
    // @ts-ignore
    style.mozTransform = v;
}

export function isVertical(tabBarPosition?: string) {
    return tabBarPosition === 'left' || tabBarPosition === 'right';
}

export function requestAnimationFrame(callback: () => void): number {
    return window.requestAnimationFrame ? window.requestAnimationFrame(callback) : window.setTimeout(callback, 1000 / 60);
}

export function getPreciseLeft(tabNode: HTMLElement, wrapperNode: HTMLElement) {
    let totalOffset = parseFloat(getComputedStyle(wrapperNode).paddingLeft) || 0;

    const calcSiblingsWidth = (node: HTMLElement) => {
        let currentNode = node;
        while (currentNode.previousElementSibling) {
            const sibling = currentNode.previousElementSibling as HTMLElement;
            const styles = getComputedStyle(sibling);
            totalOffset += sibling.offsetWidth + parseFloat(styles.marginLeft) + parseFloat(styles.marginRight);
            currentNode = sibling;
        }
    };

    if (tabNode.classList.contains('wui-tabs-text-content')) {
        const parentTab = tabNode.closest('.wui-tabs-tab') as HTMLElement;
        if (parentTab) {
            calcSiblingsWidth(parentTab);
            totalOffset += parseFloat(getComputedStyle(parentTab).marginLeft);
            const tabItem = parentTab.querySelector('.wui-tabs-tab-item') as HTMLElement;
            if (tabItem) totalOffset += parseFloat(getComputedStyle(tabItem).marginLeft) || 0;
            totalOffset += tabNode.offsetLeft;
        }
    } else {
        calcSiblingsWidth(tabNode);
        totalOffset += parseFloat(getComputedStyle(tabNode).marginLeft);
    }
    return totalOffset;
}

export function getDefaultActiveKey(props: TabsProps) {
    const { items } = props;
    if (Array.isArray(items) && items?.length > 0) {
        const found = items.find((item: any) => item.key && !item.disabled);
        return found?.key as string || '';
    }
    let activeKey = '';
    React.Children.forEach(props.children, (child: React.ReactElement) => {
        if (child && !activeKey && !child.props.disabled) {
            const key = child.key;
            if (key != null && key !== '') {
                activeKey = key as string;
            }
        }
    });
    return activeKey;
}

export function getOffsetWH(node: HTMLElement, tabBarPosition?: string): number {
    return (node as any)[isVertical(tabBarPosition) ? 'offsetHeight' : 'offsetWidth'];
}

export function getScrollWH(node: HTMLElement, tabBarPosition?: string): number {
    return (node as any)[isVertical(tabBarPosition) ? 'scrollHeight' : 'scrollWidth'];
}

export function getOffsetLT(node: HTMLElement, tabBarPosition?: string): number {
    return node.getBoundingClientRect()[isVertical(tabBarPosition) ? 'top' : 'left'];
}

export function getTabItemDom(i: number, dragable?: boolean, getRef?: any): HTMLElement {
    const nav = getRef?.('nav')?.children[0];
    return (dragable ? nav?.children[0]?.children[i] : nav?.children[i]) as HTMLElement;
}

export function checkNeedTransform(element: HTMLElement | undefined): boolean {
    if (!element) return false;
    return Math.abs(parseFloat(getComputedStyle(element).width) - element.getBoundingClientRect().width) > 1;
}

// 辅助方法：获取DOM节点
export function getDomNodes(dragable?: boolean, getRef?: any) {
    const nav = getRef?.('nav');
    if (!nav || !nav.children[0]) return [];

    return dragable && nav.children[0].children[0]?.children
        ? Array.prototype.slice.call(nav.children[0].children[0].children)
        : Array.prototype.slice.call(nav.children[0].children);
}

// 辅助方法：构建节点数组
export function buildNodeArray(domNode: any[]) {
    return domNode.map((item, index) => JSON.stringify({
        key: item.getAttribute('nodekey'),
        title: item.innerText || item.textContent,
        closable: item.getAttribute('closable'),
        index,
        fieldid: item?.getAttribute('fieldid') ? 'tabs_menu_' + item?.getAttribute('fieldid') : undefined,
        langid: item?.firstChild?.getAttribute ? item?.firstChild?.getAttribute('langid') : undefined
    }));
}

// 辅助方法：解析transform值
export function parseTransformValue(transformValue: string | undefined): string[] {
    if (!transformValue) return ['0'];
    const prefixIndex = transformValue.indexOf("(") + 1;
    return transformValue.slice(prefixIndex).split(", ");
}

// 辅助方法：计算元素尺寸（包括margin）
export function getElementTotalSize(element: HTMLElement | undefined, direction: 'horizontal' | 'vertical'): number {
    if (!element) return 0;
    const computedStyle = getComputedStyle(element);
    if (direction === 'horizontal') {
        return element.getBoundingClientRect().width +
               parseFloat(computedStyle.marginLeft) +
               parseFloat(computedStyle.marginRight);
    } else {
        return element.getBoundingClientRect().height +
               parseFloat(computedStyle.marginTop) +
               parseFloat(computedStyle.marginBottom);
    }
}

// 辅助方法：获取当前滚动位置
export function getCurrentScrollPosition(navNode: HTMLElement | undefined): number {
    if (!navNode) return 0;
    const translateValues = parseTransformValue(navNode.style?.transform);
    return -parseFloat(translateValues[0]) || 0;
}
// ============ 性能优化工具函数 ============
/**
 * DOM 属性缓存 Hook（用于组件内）
 */
export interface DOMCacheOptions {
    cacheTime?: number; // 缓存时间（毫秒）
    maxSize?: number; // 最大缓存数量
}

export class DOMCache {
    private cache: Map<string, { data: any; timestamp: number }>;
    private cacheTime: number;
    private maxSize: number;

    constructor(options: DOMCacheOptions = {}) {
        this.cache = new Map();
        this.cacheTime = options.cacheTime || 100;
        this.maxSize = options.maxSize || 100;
    }

    get(key: string): any | null {
        const cached = this.cache.get(key);
        if (!cached) return null;

        const now = Date.now();
        if (now - cached.timestamp > this.cacheTime) {
            this.cache.delete(key);
            return null;
        }

        return cached.data;
    }

    set(key: string, value: any): void {
        // 限制缓存大小
        if (this.cache.size >= this.maxSize) {
            const firstKey = this.cache.keys().next().value || '';
            this.cache.delete(firstKey);
        }

        this.cache.set(key, {
            data: value,
            timestamp: Date.now()
        });
    }

    clear(): void {
        this.cache.clear();
    }

    has(key: string): boolean {
        const cached = this.cache.get(key);
        if (!cached) return false;

        const now = Date.now();
        if (now - cached.timestamp > this.cacheTime) {
            this.cache.delete(key);
            return false;
        }

        return true;
    }
}

/**
 * 智能防抖钩子，根据调用频率动态调整延迟时间
 * @param callback 需要防抖的回调函数
 * @param deps 依赖项数组
 * @returns 防抖后的函数
 */
export const useSmartDebounce = <T extends (...args: any[]) => any>(
    callback: T,
    deps: React.DependencyList = []
) => {
    // eslint-disable-next-line no-undef
    const timeoutRef = useRef<NodeJS.Timeout>();
    const lastCallRef = useRef<number>(0);
    const delayRef = useRef<number>(50); // 初始延迟50ms

    const debouncedFn = useCallback((...args: Parameters<T>) => {
        const now = Date.now();
        const timeSinceLastCall = now - lastCallRef.current;

        // 动态调整延迟：频繁调用时增加延迟，反之减少
        if (timeSinceLastCall < 100) {
            // 如果两次调用间隔小于100ms，增加延迟（最多200ms）
            delayRef.current = Math.min(delayRef.current * 1.2, 200);
        } else {
            // 否则减少延迟（最少50ms）
            delayRef.current = Math.max(delayRef.current * 0.8, 50);
        }

        // 清除之前的定时器
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        // 设置新的定时器
        timeoutRef.current = setTimeout(() => {
            callback(...args);
            lastCallRef.current = Date.now();
        }, delayRef.current);
    }, deps);

    // 组件卸载时清理定时器
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return debouncedFn;
};